import React, { useState, useEffect } from "react";
import { getIn, useFormikContext } from "formik";
import KC_UIText from "../../../../../designsystem/typography/KC_UIText/KC_UIText";
import KC_UISmall from "../../../../../designsystem/typography/KC_UITextSmall/KC_UISmall";
import { KC_Weights } from "../../../../../designsystem/ds_commons";
import { tt } from "../../../../../utils/i18n/i18n";
import KC_Button, {
  KC_ButtonSize
} from "../../../../../designsystem/KC_Button/KC_Button";
import KC_Input from "../../../../../designsystem/KC_FormInput/KC_Input";
import KC_Toggle from "../../../../../designsystem/KC_FormInput/KC_Toggle";
import EmojiPickerWrapperJP from "../../../../../components/GiftsEditor/components/EmojiPickerWrapperJP";
import type { LoyaltyTierPrize, ExpiryType } from "kadow-jetpack-model";
import ExpiryDelaySelector from "./ExpiryDelaySelector";

export interface LoyaltyPrizeRewardEditorProps {
  prize: LoyaltyTierPrize;
  onPrizeChange: (prize: LoyaltyTierPrize) => void;
  hasAdvancedCouponOptions: boolean;
  onGenerateIdeas: () => void;
  onOfferStandardReward: () => void;
  idPrefix: string;
  children?: React.ReactNode;
}

const LoyaltyPrizeRewardEditor = ({
  prize,
  onPrizeChange,
  hasAdvancedCouponOptions,
  onGenerateIdeas,
  onOfferStandardReward,
  idPrefix,
  children
}: LoyaltyPrizeRewardEditorProps) => {
  const { errors, touched, submitCount } = useFormikContext<any>();
  const [showConditions, setShowConditions] = useState(false);

  const shouldDisplayFieldError = (fieldPath: string): boolean => {
    const fieldTouched = getIn(touched, fieldPath);
    return !!fieldTouched || submitCount > 0;
  };

  const getFieldError = (fieldPath: string): string => {
    const error = getIn(errors, fieldPath);
    if (!shouldDisplayFieldError(fieldPath) || typeof error !== "string") {
      return "";
    }
    return error;
  };

  useEffect(() => {
    if (prize.conditions && prize.conditions.length > 0) {
      setShowConditions(true);
    }
  }, [prize]);

  const toggleConditions = (newValue: boolean) => {
    setShowConditions(newValue);

    if (!newValue) {
      onPrizeChange({ ...prize, conditions: [] });
      return;
    }

    if (prize.conditions && prize.conditions.length > 0) {
      return;
    }

    onPrizeChange({
      ...prize,
      conditions: [tt("default_redeem_condition")]
    });
  };

  const setFieldValueForExpiry = (field: string, value: ExpiryType) => {
    onPrizeChange({
      ...prize,
      expiry: value as ExpiryType
    });
  };

  return (
    <div className="loyalty-gift-editor-card__expandable mt-3">
      <KC_UIText
        weight={KC_Weights.bold}
        text={prize.name || tt("prizes_configurator_no_name_gift")}
        className="text-center"
      />
      <div className="loyalty-gift-editor-card__prize-display">
        <EmojiPickerWrapperJP
          initialEmoji={prize.image || "🎁"}
          onEmojiSelect={(emoji) => onPrizeChange({ ...prize, image: emoji })}
        />
      </div>
      {!!getFieldError(`${idPrefix}.image`) && (
        <div className="error-message">
          {getFieldError(`${idPrefix}.image`)}
        </div>
      )}
      <label className="d-block mt-2">
        <KC_UIText
          weight={KC_Weights.bold}
          text={tt("jp_onboarding_step_gift_gift_name")}
        />
      </label>
      <KC_UIText
        weight={KC_Weights.reg}
        text={tt("jp_onboarding_giftform_helper_format")}
        className="text-secondary"
      />
      <div className="d-flex gap-2 align-items-center flex-wrap mt-1">
        <KC_Input
          id={`${idPrefix}-name`}
          htmlFor={`${idPrefix}.name`}
          setFieldValue={(_: string, value: unknown) =>
            onPrizeChange({ ...prize, name: String(value ?? "") })
          }
          value={prize.name ?? ""}
          placeholder={tt("jp_onboarding_giftform_placeholder_name")}
          className="flex-grow-1"
        />
        <KC_Button
          size={KC_ButtonSize.Small}
          showIconOnTheLeft={true}
          isBlack={false}
          isPrimary={false}
          className="mt-2"
          customIcon={
            <span className="loyalty-button-icon-wrap" style={{ fontSize: 16 }}>
              ⭐
            </span>
          }
          text={tt("jp_onboarding_giftform_generate_ideas")}
          type="button"
          isInverted={true}
          onClick={onGenerateIdeas}
        />
      </div>
      {hasAdvancedCouponOptions && (
        <>
          <ExpiryDelaySelector
            basePath={idPrefix}
            setFieldValue={setFieldValueForExpiry}
            value={prize.expiry?.value ?? 0}
          />
          <select
            className="form-control kc-input-wrapper"
            value={prize.unBlurDelay ?? -1}
            onChange={(e) => {
              const val = parseInt(e.target.value);
              if (!isNaN(val)) {
                onPrizeChange({ ...prize, unBlurDelay: val });
              }
            }}
          >
            <option value={1}>{tt("coupon_immediate_placeholder")}</option>
            <option value={-1}>{tt("coupon_next_day_placeholder")}</option>
          </select>
          <div className="d-flex align-items-center gap-md">
            <KC_Toggle
              id={`${idPrefix}-conditions`}
              isChecked={showConditions}
              onToggle={() => toggleConditions(!showConditions)}
            />
            <KC_UISmall
              className="h-100 d-flex align-items-center"
              weight={KC_Weights.reg}
              text={tt("jp_onboarding_step_gift_toggle_label_condition")}
            />
          </div>
          {showConditions && (
            <div className="d-flex flex-column st-gap">
              <label>
                <KC_UIText
                  weight={KC_Weights.bold}
                  text={tt("jp_onboarding_step_gift_label_condition")}
                />
              </label>
              <KC_Input
                id={`${idPrefix}-condition-0`}
                htmlFor={`${idPrefix}.conditions[0]`}
                setFieldValue={(_: string, value: unknown) => {
                  const conditions = [...(prize.conditions ?? [])];
                  conditions[0] = String(value ?? "");
                  onPrizeChange({ ...prize, conditions });
                }}
                value={prize.conditions?.[0] ?? ""}
                placeholder={tt(
                  "jp_onboarding_step_gift_placeholder_condition_01"
                )}
                required={showConditions}
              />
              <KC_Input
                id={`${idPrefix}-condition-1`}
                htmlFor={`${idPrefix}.conditions[1]`}
                setFieldValue={(_: string, value: unknown) => {
                  const conditions = [...(prize.conditions ?? [])];
                  conditions[1] = String(value ?? "");
                  onPrizeChange({ ...prize, conditions });
                }}
                value={prize.conditions?.[1] ?? ""}
                placeholder={tt(
                  "jp_onboarding_step_gift_placeholder_condition_02"
                )}
              />
              <KC_Input
                id={`${idPrefix}-condition-2`}
                htmlFor={`${idPrefix}.conditions[2]`}
                setFieldValue={(_: string, value: unknown) => {
                  const conditions = [...(prize.conditions ?? [])];
                  conditions[2] = String(value ?? "");
                  onPrizeChange({ ...prize, conditions });
                }}
                value={prize.conditions?.[2] ?? ""}
                placeholder={tt(
                  "jp_onboarding_step_gift_placeholder_condition_03"
                )}
              />
            </div>
          )}
        </>
      )}
      {children}
      <button
        type="button"
        onClick={onOfferStandardReward}
        className="border-0 bg-transparent p-0 text-start w-100 mt-3 cursor-pointer"
        style={{ textDecoration: "underline" }}
      >
        {tt("loyalty_delete_prize_offer_tokens")}
      </button>
    </div>
  );
};

export default LoyaltyPrizeRewardEditor;
